'use client'

import { type FC } from 'react'

import { type OfferV11, type PimProductV11 } from '@redteclab/api/clients/bully'
import { type GkAirRecommendedProductWeb } from '@redteclab/api/clients/crosssellers-proxy-service'
import { IconPlus } from '@redteclab/icons'

import { useGlobalConfigContext } from '../../../../global-config'
import { gtmDataLayerPushHomeOneAddToCart } from '../../../../gtm'
import {
  gtmDataLayerPushRecommendationTracking,
  recommendationAccessorGetLocalizedProductImageUrl,
  recommendationAccessorGetLocalizedProductName,
  recommendationAccessorGetLocalizedProductUrl,
  RecommendationWidgetTitle,
  useRecommendationWidgetContext,
} from '../../../../recommendation'
import { productAccessorGetRedPointsRedPointsAmountWithMultiplier } from '../../../model/productAccessor/productAccessorGetRedPointsRedPointsAmountWithMultiplier'

import { ProductRecommendationWidgetBuyTogetherAddToCart } from './ProductRecommendationAreaBuyTogetherAddToCart'
import { ProductRecommendationWidgetBuyTogetherPrices } from './ProductRecommendationAreaBuyTogetherPrices'
import { ProductRecommendationAreaProductThumbnail } from './ProductRecommendationAreaProductThumbnail'

type ProductRecommendationWidgetBuyTogetherContentProps = {
  currentOffer: OfferV11
  currentVariant: PimProductV11
  recommendedProductWithAttributes?: GkAirRecommendedProductWeb
}

export const ProductRecommendationWidgetBuyTogetherContent: FC<
  ProductRecommendationWidgetBuyTogetherContentProps
> = ({ currentOffer, currentVariant, recommendedProductWithAttributes }) => {
  const { locale } = useGlobalConfigContext()
  const { widgetId } = useRecommendationWidgetContext()
  const recommendedProduct = recommendedProductWithAttributes?.data

  return (
    <>
      <RecommendationWidgetTitle className="mb-3.5 text-xl font-medium tablet:text-2xl" />
      <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
        className="relative mb-7 flex items-center rounded-m border border-dark-primary-min p-3.5 after:absolute after:bottom-[-21px] after:left-1/2 after:size-0 after:-translate-x-1/2 after:border after:border-x-[21px] after:border-t-[21px] after:border-transparent after:border-t-[var(--background-light-primary-medium)]"
      >
        <ProductRecommendationAreaProductThumbnail
          className="flex-1 self-start"
          imageUrl={currentVariant.images?.[0]}
          productId={currentVariant.upid}
          productName={currentVariant.name}
          productUrl={currentVariant.canonical_url}
          recommendedProduct={recommendedProduct}
          redpoints={productAccessorGetRedPointsRedPointsAmountWithMultiplier(
            currentOffer,
          )}
        />

        <div className="flex flex-1 justify-center">
          <IconPlus className="size-8 fill-dark-brand" />
        </div>
        <ProductRecommendationAreaProductThumbnail
          className="flex-1 self-start"
          imageAsUrl
          imageUrl={
            recommendedProduct
              ? recommendationAccessorGetLocalizedProductImageUrl({
                  locale,
                  product: recommendedProduct,
                })
              : ''
          }
          onGtmDataLayerPush={() => {
            gtmDataLayerPushRecommendationTracking({
              productId: recommendedProduct?.UID,
              trackingToken:
                recommendedProductWithAttributes?.attributes.trackingtoken,
              widgetId,
              widgetProductId: currentVariant.upid,
            })
          }}
          position={1}
          product={recommendedProductWithAttributes}
          productId={recommendedProduct ? recommendedProduct.UID : ''}
          productName={recommendationAccessorGetLocalizedProductName({
            locale,
            product: recommendedProduct,
          })}
          productUrl={
            recommendedProduct
              ? recommendationAccessorGetLocalizedProductUrl({
                  locale,
                  product: recommendedProduct,
                })
              : ''
          }
          recommendedProduct={recommendedProduct}
        />
      </div>
      <div className="flex justify-center">
        <ProductRecommendationWidgetBuyTogetherPrices
          recommendedProduct={recommendedProduct}
        />
      </div>
      <ProductRecommendationWidgetBuyTogetherAddToCart
        currentOffer={currentOffer}
        currentVariant={currentVariant}
        onGtmDataLayerPush={() => {
          gtmDataLayerPushHomeOneAddToCart()
          gtmDataLayerPushRecommendationTracking({
            productId: recommendedProduct?.UID,
            trackingToken:
              recommendedProductWithAttributes?.attributes.trackingtoken,
            widgetId,
            widgetProductId: currentVariant.upid,
          })
        }}
        recommendedProduct={recommendedProduct}
      />
    </>
  )
}
